/**
 * #454 — operator-facing copy for the PEOC's MULTI-community COORDINATION INBOX (the discovery
 * surface over the #420a threads). Verbatim Ontario IMS 2.0 §4.2 COORDINATION language — proofread
 * by the standing IMS doctrine gate at build acceptance + the existing US-ICS ontarioImsVocabulary
 * lock; NO dedicated dignity gate (founder ruling 2026-07-08 — vocabulary is verbatim doctrine, so
 * the standing gate is sufficient; no new forbidden-term lock).
 *
 * DOCTRINE:
 *   • COORDINATION, not command (Ontario IMS §4.2 — the PEOC "maintains a regular communication
 *     cycle with host and evacuated communities"). No command / order / direct framing; the inbox
 *     is the operator's OWN coordination queue. It reuses the #420a "Operator coordination — not
 *     resident-facing" register (shared constant), and treats every community identically.
 *   • SENT≠READ HONESTY (#453): the per-row count is the operator's OWN not-yet-ACKNOWLEDGED
 *     messages — the #453 explicit-acknowledge state, so it reads "N to acknowledge", never
 *     "N unread"/"N read" (a message can be on screen without an acknowledge).
 *   • UNKNOWN ≠ ZERO: a failed load renders the ERROR state (the panel's three-state), never the
 *     all-quiet empty state and never a "0" tab badge (the badge derives from a positive count).
 *
 * Kept in its own module (no React import) so the copy has a single source — the codebase idiom
 * (interopsVocabulary.ts / communityCommsVocabulary.ts).
 */

/* ── The sub-tab + its framing ────────────────────────────────────────────── */

/** The Coordination sub-tab label (NOT "Inbox" — that tab is the agency-task worklist). */
export const COMMUNITY_COORDINATION_TAB_LABEL = 'Community coordination';

/** The panel intro — frames it as the OPERATOR's own queue ("waiting on you"), never a watch on
 *  the community. */
export const COMMUNITY_COORDINATION_INTRO =
  'Communities you are coordinating with that have messages waiting on you, plus recent threads. Open one to jump into its coordination log.';

/* ── The honest three-state (copied from ShiftLogPanel's error/loading/empty) ─ */

/** loading ⇒ shown only until the first load resolves (a background refresh keeps the list). */
export const COMMUNITY_COORDINATION_LOADING = 'Loading community coordination…';

/** loaded + empty ⇒ a genuine no-traffic result (NEVER shown on error). */
export const COMMUNITY_COORDINATION_EMPTY = 'No communities have coordination traffic.';

/** error ⇒ a rejected load renders THIS (never the empty/all-quiet state, never a "0" badge). */
export const COMMUNITY_COORDINATION_ERROR = 'Could not load community coordination';

/* ── Per-row copy ─────────────────────────────────────────────────────────── */

/** The per-row count — the operator's OWN not-yet-ACKNOWLEDGED messages (#453 acknowledge state,
 *  never "unread"/"read"). Compact chip form: "N to acknowledge". */
export function awaitingAcknowledgementLabel(n: number): string {
  return `${n} to acknowledge`;
}

/** The thread's most-recent message time: "Last message · <time>". Deliberately avoids
 *  "activity"/"status" framing (FN-dignity). */
export function lastMessageLabel(time: string): string {
  return `Last message · ${time}`;
}

/** Display label for a community_type. Every type renders in the SAME neutral chip — a
 *  First-Nation community is a full peer of a municipal one (no FN-only styling). */
export function communityTypeLabel(communityType: string): string {
  switch (communityType) {
    case 'municipality':
      return 'Municipality';
    case 'first_nation':
      return 'First Nation';
    case 'unincorporated':
      return 'Unincorporated';
    case 'metis_settlement':
      return 'Métis settlement';
    case 'inuit_community':
      return 'Inuit community';
    default:
      return 'Community';
  }
}
